import sumBy from 'lodash/sumBy';
import type { CampusApplicationFormSubmissionResult } from '../../../Api/CampusApplicationForm/CampusApplicationFormSubmissionResult.csharp';
import type { ApplicationChoiceDto } from '../../../Search/ApplicationChoice/Controller/ApplicationChoiceDto.csharp';
import type { GoogleTagManagerEcommerceProduct } from '../GoogleTagManagerModels';
import { GtmCommerceEventsHandler } from '../GtmCommerceEventsHandler';
import { CampusBrand, VocationalSchoolBrand } from '~/Partials/GoogleTagManager/AnalyticsConstants';

export class CampusAnalytics {
    public static addProducts = (items: ApplicationChoiceDto[]) => {
        const products = items.map(CampusAnalytics.createProductFromApplicationChoice);
        const actionField = CampusAnalytics.createBaseActionFieldFromApplicationChoice(items);

        if (products.length > 0) {
            GtmCommerceEventsHandler.pushAddProducts(products, actionField);
        }
    };

    public static removeProducts = (items: ApplicationChoiceDto[]) => {
        const products = items.map(CampusAnalytics.createProductFromApplicationChoice);
        const actionField = CampusAnalytics.createBaseActionFieldFromApplicationChoice(items);

        if (products.length > 0) {
            GtmCommerceEventsHandler.pushRemoveProducts(products, actionField);
        }
    };

    public static checkout = (stepNumber: number, items: ApplicationChoiceDto[]) => {
        const products = items.map(CampusAnalytics.createProductFromApplicationChoice);
        const baseActionField = CampusAnalytics.createBaseActionFieldFromApplicationChoice(items);

        if (products.length > 0) {
            if (stepNumber === 1) {
                GtmCommerceEventsHandler.pushBeginCheckout(products, baseActionField);
            } else {
                const checkoutActionField = {
                    ...baseActionField,
                    checkout_step: stepNumber,
                };
                GtmCommerceEventsHandler.pushCheckout(products, checkoutActionField);
            }
        }
    };

    public static applicationResult = (data: CampusApplicationFormSubmissionResult) => {
        const products = data.choices.map(CampusAnalytics.createProductFromApplicationChoice);
        const actionField = CampusAnalytics.createPurchaseActionFieldFromCampusApplication(data);

        GtmCommerceEventsHandler.pushPurchase(products, actionField);
    };

    private static createBaseActionFieldFromApplicationChoice = (items: ApplicationChoiceDto[]) => ({
        currency: '',
        value: sumBy(items, item => item.price ?? 0),
    });

    private static createProductFromApplicationChoice = (choice: ApplicationChoiceDto) => {
        const studyType = choice.level === VocationalSchoolBrand ? VocationalSchoolBrand : CampusBrand;

        return {
            item_id: choice.id,
            item_name: choice.name,
            affiliation: studyType,
            // `?.` added to prevent errors in case of form progress stored in local storage.
            // We can probably wait 1-2 semesters to be completely safe and remove that.
            item_brand: choice.educationCategories?.join(', ') ?? '',
            item_category: choice.studyFormType,
            item_category2: choice.points,
            item_category3: choice.label,
            item_variant: choice.level,
            // default values set on a base of consultations with client
            price: 100,
            quantity: 1,
        } satisfies GoogleTagManagerEcommerceProduct;
    };

    private static createPurchaseActionFieldFromCampusApplication = (
        result: CampusApplicationFormSubmissionResult
    ) => ({
        currency: '',
        value: sumBy(result.choices, item => item.price ?? 0),
        transaction_id: result.requestId,
        coupon: '',
        shipping: 0,
        tax: 0,
    });
}
